import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import Switch from 'react-switch';
import { FieldError } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../store/reducers/snackbarSlice';
import { Company, WorkTime } from '../../../types';
import axiosInstance from '../../../utils/axiosInstance';
import DefaultAvatar from '../icons/defaultAvatar';
import style from './CompanyForm.module.css';
import DateModal from './DateModal/DateModal';
import FieldController from './FieldController/FieldController';
import FormHeader from './FormHeader/FormHeader';
import FormSection from './FormSection/FormSection';
import { useCompanyForm } from './hooks/useCompanyForm';
import EmployeesIcon from './icon/EmployeesIcon';
import Input from './input/input';
import TagSelector from './TagSelector/TagSelector';
import WorkTimeModal from './WorkTimeModal/WorkTimeModal';

const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" />
  </svg>
);
const TrashIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);
const SendIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const EmailIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </svg>
);
const PhoneFieldIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const QrIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3M21 14v7h-7M17 21v-4" />
  </svg>
);
const NfcIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="3" />
    <path d="M7 10.5a5 5 0 0 1 0 3" />
    <path d="M10 8.5a9 9 0 0 1 0 7" />
    <path d="M13.5 7a12 12 0 0 1 0 10" />
  </svg>
);
const PinIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

interface CompanyFormProps {
  companyData?: Company;
  onClose: () => void;
  onSave: (company: Company) => void;
  isEdit?: boolean;
  onOpenEmployeesModal?: (id: number) => void;
  onSendInvitation?: (id: number) => void;
  onWidgetQr?: (id: number) => void;
  onTabletQr?: (id: number) => void;
  onEmailHistory?: (id: number) => void;
  onLocations?: (id: number) => void;
  onDelete?: (id: number) => void;
  /** §FB37: рендер як вкладка сторінки компанії — без фіксованого оверлею, без власного
   *  хедера (його дає сторінка) і без нижнього ряду сервісних кнопок (їх замінюють вкладки). */
  embedded?: boolean;
  /** §FB39: сторінка рендерить «Зберегти/Видалити» у своєму хедері — формі треба
   *  повідомляти, чи є незбережені зміни і чи валідна форма. */
  onFormStateChange?: (state: { dirty: boolean; valid: boolean }) => void;
  formDataArray?: any[];
  newPlan?: string | number;
  requestPlanId?: any;
  onUpdate?: () => void;
  handleUpdatePlans?: () => void;
  isPlanComponent?: boolean;
  /** §FB170: миттєві перемикачі змінили компанію — сторінка оновлює свій стан, щоб інші
   *  вкладки («Співробітники», «QR для віджету») бачили зміну без F5. */
  onCompanyPatched?: (patch: Partial<Company>) => void;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  companyData,
  onClose,
  onSave,
  isEdit = false,
  onOpenEmployeesModal,
  onSendInvitation,
  onWidgetQr,
  onTabletQr,
  onEmailHistory,
  onLocations,
  onDelete,
  embedded = false,
  onFormStateChange,
  formDataArray,
  newPlan,
  requestPlanId,
  handleUpdatePlans,
  isPlanComponent = false,
  onCompanyPatched,
}) => {
  // §FB81: master-switch «Веб для відповідальних» — миттєвий PATCH, окремо від форми.
  const [webLiaisonOn, setWebLiaisonOn] = useState<boolean>(
    Boolean(companyData?.webLiaisonEnabled),
  );
  const [webLiaisonBusy, setWebLiaisonBusy] = useState(false);
  useEffect(() => {
    setWebLiaisonOn(Boolean(companyData?.webLiaisonEnabled));
  }, [companyData?.webLiaisonEnabled]);
  const handleWebLiaisonToggle = async (checked: boolean) => {
    if (!companyData?.id || webLiaisonBusy) return;
    setWebLiaisonBusy(true);
    setWebLiaisonOn(checked);
    try {
      await axiosInstance.patch('/LiaisonAdmin/SetCompanyWebLiaison', {
        companyId: companyData.id,
        enabled: checked,
      });
      onCompanyPatched?.({ webLiaisonEnabled: checked });
    } catch (error) {
      setWebLiaisonOn(!checked); // відкат при помилці
      console.error('Error updating web liaison switch:', error);
    } finally {
      setWebLiaisonBusy(false);
    }
  };

  // §FB175 (FR-11.1): дзвінок із браузера — ознаки компанії («Дзвінки з браузера» для посилання віджета, «Таблички
  // в браузері» для табличок) і загальний вимикач. Миттєвий PATCH, окремо від форми; діє з наступного дзвінка, без
  // свапу. Старий бекенд без цих точок — блоку просто немає.
  const [browserCalls, setBrowserCalls] = useState<{
    global: boolean;
    calls: boolean;
    plates: boolean;
  } | null>(null);
  const [browserBusy, setBrowserBusy] = useState(false);
  useEffect(() => {
    if (!isEdit || !companyData?.id) return undefined;
    let cancelled = false;
    axiosInstance
      .get('/LiaisonAdmin/GetBrowserCalls', {
        params: { companyId: companyData.id },
      })
      .then((res) => {
        if (cancelled) return;
        setBrowserCalls({
          global: Boolean(res.data?.globalEnabled),
          calls: Boolean(res.data?.browserCalls),
          plates: Boolean(res.data?.browserPlates),
        });
      })
      .catch(() => {
        if (!cancelled) setBrowserCalls(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, companyData?.id]);
  const handleBrowserFlag = async (
    key: 'calls' | 'plates',
    checked: boolean,
  ) => {
    if (!companyData?.id || !browserCalls || browserBusy) return;
    const before = browserCalls;
    setBrowserBusy(true);
    setBrowserCalls({ ...before, [key]: checked });
    try {
      await axiosInstance.patch('/LiaisonAdmin/SetCompanyBrowserCalls', {
        companyId: companyData.id,
        browserCalls: key === 'calls' ? checked : null,
        browserPlates: key === 'plates' ? checked : null,
      });
    } catch (error) {
      setBrowserCalls(before); // відкат при помилці
      dispatch(
        showSnackbar({
          message: 'Не вдалося змінити ознаку дзвінків із браузера',
          type: 'error',
        }),
      );
      console.error('Error updating browser calls flag:', error);
    } finally {
      setBrowserBusy(false);
    }
  };
  const handleBrowserGlobal = async (checked: boolean) => {
    if (!browserCalls || browserBusy) return;
    // Вимикач спільний для всіх компаній — без підтвердження не чіпаємо.
    const ok = window.confirm(
      checked
        ? 'Увімкнути дзвінки з браузера? Вони з’являться в усіх компаніях, де ввімкнено ознаку «Дзвінки з браузера» чи «Таблички в браузері».'
        : 'Вимкнути дзвінки з браузера для всіх компаній? Посилання знову працюватимуть, як до FB175; дзвінки, що вже йдуть, не обірвуться.',
    );
    if (!ok) return;
    const before = browserCalls;
    setBrowserBusy(true);
    setBrowserCalls({ ...before, global: checked });
    try {
      await axiosInstance.patch('/LiaisonAdmin/SetBrowserCallsGlobal', {
        enabled: checked,
      });
    } catch (error) {
      setBrowserCalls(before);
      dispatch(
        showSnackbar({
          message: 'Не вдалося змінити загальний вимикач',
          type: 'error',
        }),
      );
      console.error('Error updating browser calls global switch:', error);
    } finally {
      setBrowserBusy(false);
    }
  };
  const {
    control,
    handleSubmit,
    errors,
    isValid,
    openDateModal,
    closeDateModal,
    isDateModalOpen,
    handleDateSelect,
    openWorkTimeModal,
    closeWorkTimeModal,
    isWorkTimeModalOpen,
    handleWorkTimeSave,
    onSubmit,
    isDirty,
    watch,
    selectedTags,
    handleTagChange,
    trigger,
    setValue,
    touchedFields,
  } = useCompanyForm(
    'companyData',
    companyData,
    undefined,
    isEdit,
    requestPlanId,
    handleUpdatePlans,
    isPlanComponent,
  );

  const timeDocumentFrom = watch('contractStartAt');
  const timeDocumentTo = watch('contractFinishAt');
  const planId = watch('planId');
  const contractNumber = watch('contractNumber');
  const dispatch = useDispatch();

  const [plans, setPlans] = useState<{ value: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<any[]>(formDataArray || []);
  const [formattedWorkTime, setFormattedWorkTime] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string>(
    companyData?.avatarUrl || '',
  );
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasFetchedPlans = useRef(false);

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !companyData?.id) return;
    setLogoPreview(URL.createObjectURL(file));
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      await axiosInstance.post(
        `/File/UploadCompanyAvatar?companyOwnerId=${companyData.id}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      dispatch(showSnackbar({ message: 'Логотип оновлено', type: 'success' }));
    } catch {
      dispatch(
        showSnackbar({
          message: 'Не вдалося завантажити логотип',
          type: 'error',
        }),
      );
    }
  };
  const formattedDateRange =
    timeDocumentFrom && timeDocumentTo
      ? `з ${new Date(timeDocumentFrom).toLocaleDateString()} по ${new Date(timeDocumentTo).toLocaleDateString()}`
      : '';

  const handleFormSubmit = async (data: Company) => {
    try {
      const savedData = await onSubmit(data);
      onSave(savedData);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response && error.response.status === 400) {
          const errorMessage =
            error.response.data?.Message || error.response.data?.message;
          if (
            errorMessage &&
            errorMessage.toLowerCase().includes('user already exists')
          ) {
            dispatch(
              showSnackbar({
                message: 'Така компанія вже зареєстрована',
                type: 'error',
              }),
            );
          } else {
            dispatch(
              showSnackbar({
                message: 'Помилка запиту: Некоректні дані',
                type: 'error',
              }),
            );
          }
        } else {
          dispatch(
            showSnackbar({
              message: 'Сталася помилка при збереженні компанії',
              type: 'error',
            }),
          );
        }
      }
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    return `${hours}:${minutes}`;
  };

  const formatWorkTime = (workTime: WorkTime[]): string => {
    if (!workTime.length) return '';

    const daysOfWeekShort = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
    const firstDay = workTime[0];
    const lastDay = workTime[workTime.length - 1];

    const firstDayIndex = firstDay.dayOfTheWeek - 1;
    const lastDayIndex = lastDay.dayOfTheWeek - 1;

    if (workTime.length === 1) {
      return `${daysOfWeekShort[firstDayIndex]} ${formatTime(firstDay.timeStart)} - ${formatTime(firstDay.timeEnd)}`;
    }

    return `${daysOfWeekShort[firstDayIndex]} - ${daysOfWeekShort[lastDayIndex]} ${formatTime(firstDay.timeStart)} - ${formatTime(firstDay.timeEnd)}`;
  };

  const handleWorkTimeSaveAndUpdate = (workTime: WorkTime[]) => {
    handleWorkTimeSave(workTime);
    setFormattedWorkTime(formatWorkTime(workTime));
  };

  const handleOpenEmployees = () => {
    if (onOpenEmployeesModal && companyData?.id) {
      onOpenEmployeesModal(companyData.id);
    }
  };

  const handleWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const fetchPlans = async () => {
    if (hasFetchedPlans.current || loading) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get('/Plans/GetPlansList');
      setPlans(response.data);
      hasFetchedPlans.current = true;
      setError(null);
    } catch (err) {
      setError('Помилка завантаження планів');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedPlans.current && !loading) {
      fetchPlans();
    }
  }, [loading]);

  useEffect(() => {
    if (formDataArray && formDataArray.length > 0) {
      setFormData(formDataArray);
    }
  }, [formDataArray]);

  useEffect(() => {
    if (companyData?.workTime) {
      setFormattedWorkTime(formatWorkTime(companyData.workTime));
    }
  }, [companyData?.workTime]);

  useEffect(() => {
    if (isPlanComponent) {
      trigger();
    }
  }, [isPlanComponent, trigger]);

  useEffect(() => {
    if (companyData) {
      setValue('email', companyData?.email || '');
      setValue('edrpou', companyData?.edrpou || '');
      setValue('phone', companyData?.phone || '');
      setValue('iban', companyData?.iban || '');
    }

    setTimeout(() => {
      trigger(['email', 'edrpou', 'phone', 'iban']);
    }, 0);
  }, [companyData, setValue, trigger]);

  const email = watch('email');
  const phone = watch('phone');
  const edrpou = watch('edrpou');
  const name = watch('name');
  const iban = watch('iban');
  const contract = watch('contractNumber');

  const isFormValid =
    isValid && email && phone && edrpou && name && iban && contract;

  // §FB39: сторінка компанії вмикає «Зберегти» у своєму хедері лише коли є зміни.
  useEffect(() => {
    onFormStateChange?.({ dirty: !!isDirty, valid: !!isFormValid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isFormValid]);

  return (
    <div
      className={embedded ? style.embeddedWrap : style.formWrapper}
      onClick={embedded ? undefined : handleWrapperClick}
    >
      <div className={embedded ? style.embeddedCard : style.modalWrap}>
        {!embedded && (
          <FormHeader
            title={isEdit ? `Компанія: ${companyData?.name}` : 'Нова компанія'}
            onClose={onClose}
          />
        )}
        <form
          id={embedded ? 'company-info-form' : undefined}
          className={style.companyForm}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(handleFormSubmit)(e);
          }}
        >
          <FormSection
            title="Інформація про компанію"
            subtitle="Основні дані та контактна інформація"
            icon={
              <span
                className={style.logoUpload}
                onClick={() => logoInputRef.current?.click()}
                title="Натисніть, щоб змінити логотип"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" className={style.logoImg} />
                ) : (
                  <DefaultAvatar />
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoSelect}
                  style={{ display: 'none' }}
                />
              </span>
            }
            headerCenter={
              /* §FB31: Видалити/Зберегти — угорі; §FB39: на сторінці компанії ці
                 кнопки живуть у хедері сторінки, тож у embedded їх тут немає. */
              embedded ? undefined : (
                <div className={style.topActions}>
                  {isEdit && onDelete && (
                    <button
                      className={style.deleteButton}
                      type="button"
                      onClick={() =>
                        companyData?.id && onDelete?.(companyData.id)
                      }
                    >
                      <TrashIcon />
                      <span>Видалити</span>
                    </button>
                  )}
                  <button
                    className={style.submitButton}
                    type="submit"
                    disabled={!isFormValid}
                  >
                    <CheckIcon />
                    <span>
                      {isEdit ? 'Зберегти' : 'Зберегти та надіслати запрошення'}
                    </span>
                  </button>
                </div>
              )
            }
            headerRight={
              <TagSelector
                selectedTags={selectedTags}
                onChange={handleTagChange}
              />
            }
          >
            <div className={style.displayTop}>
              <FieldController
                name="name"
                control={control}
                rules={{
                  maxLength: {
                    value: 80,
                    message: 'Максимальна кількість символів 80',
                  },
                }}
                type="text"
                label="Назва"
                error={errors.name}
              />
              <div onClick={openWorkTimeModal}>
                <FieldController
                  name="workTime"
                  control={control}
                  type="text"
                  label="Години роботи"
                  value={formattedWorkTime}
                  error={errors.workTime as FieldError | undefined}
                  readOnly
                  rules={undefined}
                />
              </div>
              <FieldController
                name="edrpou"
                control={control}
                type="text"
                label="Код ЄДРПОУ"
                error={errors.edrpou}
                rules={{
                  maxLength: {
                    value: 10,
                    message: 'Код ЄДРПОУ має бути не більше 10 символів',
                  },
                }}
              />
            </div>
            <div className={style.displayMiddle}>
              <FieldController
                name="userName"
                control={control}
                type="text"
                label="ПІБ керівника"
                error={errors.userName}
                rules={{
                  maxLength: {
                    value: 64,
                    message: 'Максимальна кількість символів 64',
                  },
                }}
              />
              <FieldController
                name="directorPosition"
                control={control}
                type="text"
                label="Посада керівника"
                error={errors.directorPosition}
                rules={{
                  maxLength: {
                    value: 64,
                    message: 'Максимальна кількість символів 64',
                  },
                }}
              />
              <FieldController
                name="iban"
                control={control}
                type="text"
                label="Рахунок / IBAN"
                error={errors.iban}
                rules={{
                  maxLength: {
                    value: 29,
                    message: 'Рахунок / IBAN має бути не більше 29 символів',
                  },
                }}
              />
            </div>
            <div className={style.displayBottom}>
              <FieldController
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: 'Введіть дійсний email',
                  },
                  maxLength: {
                    value: 256,
                    message: 'Email має бути не більше 256 символів',
                  },
                }}
                type="email"
                label="Email"
                error={errors.email}
                icon={<EmailIcon />}
              />
              <FieldController
                name="phone"
                control={control}
                rules={{
                  pattern: {
                    value: /^\+\d{12}$/,
                    message:
                      'Номер телефону має бути у форматі +38 000 000 00 00',
                  },
                }}
                type="tel"
                label="Телефон"
                error={errors.phone}
                icon={<PhoneFieldIcon />}
              />
              <FieldController
                name="website"
                control={control}
                type="text"
                label="Вебсторінка"
                error={errors.website}
                rules={{
                  maxLength: {
                    value: 256,
                    message: 'Вебсторінка має бути не більше 256 символів',
                  },
                }}
              />
            </div>
          </FormSection>
          <FormSection
            title="Інформація про підписку"
            subtitle="Тариф та умови договору"
            icon={<StarIcon />}
          >
            <div className={style.displayTop}>
              <FieldController
                name="plan"
                control={control}
                type="select"
                label="Тариф"
                options={plans.map((plan) => ({
                  value: plan.value.toString(),
                  label: plan.name,
                }))}
                error={errors.plan}
                value={watch('plan') || ''}
              />
              <FieldController
                name="contractNumber"
                control={control}
                type="text"
                label="Номер договору"
                error={errors.contractNumber}
              />
              <div onClick={openDateModal}>
                <Input
                  type="text"
                  label="Термін договору"
                  value={formattedDateRange}
                  readOnly
                  name="timeContractRange"
                  onChange={() => {}}
                  onBlur={() => {}}
                />
              </div>
            </div>
            {isEdit && companyData?.id && (
              <label className={style.webLiaisonRow}>
                <Switch
                  onChange={handleWebLiaisonToggle}
                  checked={webLiaisonOn}
                  disabled={webLiaisonBusy}
                  onColor="#0a8080"
                  offColor="#ccc"
                  onHandleColor="#f2f8f8"
                  offHandleColor="#fff"
                  checkedIcon={false}
                  uncheckedIcon={false}
                  height={22}
                  width={44}
                />
                <span>
                  Веб для відповідальних
                  <small>
                    Вебкабінет запланованих зустрічей (liaison). Вимкнення
                    миттєво закриває вебдоступ усім відповідальним компанії.
                  </small>
                </span>
              </label>
            )}
            {isEdit && companyData?.id && browserCalls && (
              <div className={style.browserCalls}>
                <div className={style.browserCallsTitle}>
                  Дзвінок із браузера
                </div>
                <label className={style.webLiaisonRow}>
                  <Switch
                    onChange={(checked) =>
                      void handleBrowserFlag('calls', checked)
                    }
                    checked={browserCalls.calls}
                    disabled={browserBusy}
                    onColor="#0a8080"
                    offColor="#ccc"
                    onHandleColor="#f2f8f8"
                    offHandleColor="#fff"
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={22}
                    width={44}
                  />
                  <span>
                    Дзвінки з браузера
                    <small>
                      Посилання віджета: на комп’ютері дзвінок одразу в
                      браузері, на телефоні — вибір «застосунок чи браузер». Діє
                      з наступного дзвінка.
                    </small>
                  </span>
                </label>
                <label className={style.webLiaisonRow}>
                  <Switch
                    onChange={(checked) =>
                      void handleBrowserFlag('plates', checked)
                    }
                    checked={browserCalls.plates}
                    disabled={browserBusy}
                    onColor="#0a8080"
                    offColor="#ccc"
                    onHandleColor="#f2f8f8"
                    offHandleColor="#fff"
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={22}
                    width={44}
                  />
                  <span>
                    Таблички в браузері
                    <small>
                      Посилання табличок на локаціях: так само, дзвінок удвох із
                      перекладачем.
                    </small>
                  </span>
                </label>
                <label className={style.webLiaisonRow}>
                  <Switch
                    onChange={(checked) => void handleBrowserGlobal(checked)}
                    checked={browserCalls.global}
                    disabled={browserBusy}
                    onColor="#0a8080"
                    offColor="#ccc"
                    onHandleColor="#f2f8f8"
                    offHandleColor="#fff"
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={22}
                    width={44}
                  />
                  <span>
                    Загальний вимикач — для всіх компаній
                    <small>
                      {browserCalls.global
                        ? 'Увімкнено: дзвінки з браузера працюють у компаніях з ознаками вище.'
                        : 'Вимкнено: дзвінків із браузера немає ні в кого, хоч би які ознаки компаній.'}
                      {(browserCalls.calls || browserCalls.plates) &&
                      !browserCalls.global
                        ? ' Ознаки цієї компанії почнуть діяти лише після ввімкнення.'
                        : ''}
                    </small>
                  </span>
                </label>
              </div>
            )}
          </FormSection>
          {/* §FB31: Видалити/Зберегти переїхали в шапку — тут лишились сервісні кнопки.
              §FB37: у embedded-режимі їх замінюють вкладки сторінки компанії. */}
          {isEdit && !embedded && (
            <div className={style.buttonEdit}>
              <div className={style.actionButtons}>
                <button
                  className={style.employeesButton}
                  type="button"
                  onClick={handleOpenEmployees}
                >
                  <EmployeesIcon />
                  <span>Співробітники</span>
                </button>
                <button
                  className={style.employeesButton}
                  type="button"
                  onClick={() =>
                    companyData?.id && onLocations?.(companyData.id)
                  }
                >
                  <PinIcon />
                  <span>Локації</span>
                </button>
                <button
                  className={style.employeesButton}
                  type="button"
                  onClick={() =>
                    companyData?.id && onWidgetQr?.(companyData.id)
                  }
                >
                  <QrIcon />
                  <span>QR для віджету</span>
                </button>
                <button
                  className={style.employeesButton}
                  type="button"
                  onClick={() =>
                    companyData?.id && onTabletQr?.(companyData.id)
                  }
                >
                  <NfcIcon />
                  <span>QR/NFC</span>
                </button>
                <button
                  className={style.employeesButton}
                  type="button"
                  onClick={() =>
                    companyData?.id && onSendInvitation?.(companyData.id)
                  }
                >
                  <SendIcon />
                  <span>Надіслати запрошення</span>
                </button>
                <button
                  className={style.employeesButton}
                  type="button"
                  onClick={() =>
                    companyData?.id && onEmailHistory?.(companyData.id)
                  }
                >
                  <EmailIcon />
                  <span>Історія листів</span>
                </button>
              </div>
            </div>
          )}
        </form>
        <DateModal
          isOpen={isDateModalOpen}
          onClose={closeDateModal}
          onSelect={handleDateSelect}
        />
        <WorkTimeModal
          isOpen={isWorkTimeModalOpen}
          onClose={closeWorkTimeModal}
          workTime={companyData?.workTime || []}
          onSave={handleWorkTimeSaveAndUpdate}
        />
      </div>
    </div>
  );
};

export default CompanyForm;
